import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Plus, Shield, Users, X } from 'lucide-react';
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, EmptyState, Field, Input, NativeSelect, Textarea } from '@vit/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { KeyLabel, PhaseBadge } from '../../components/badges.js';
import { PageHeader, PrincipalPicker, QueryState, SectionTitle, UserChip } from '../../components/common.js';

export function TeamsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const teams = useApiQuery('listTeams', { query: {} });
  const [open, setOpen] = useState(false);
  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('teams.title')} actions={me.permissions.canCreateTeam ? <Button variant="primary" onClick={() => setOpen(true)}><Plus /> {t('teams.new')}</Button> : null} />
      <div className="p-4 md:p-6">
        <QueryState query={teams} empty={teams.data?.data.length === 0 ? <EmptyState icon={<Users />} title={t('common.noResults')} /> : undefined}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {(teams.data?.data ?? []).map((team) => (
              <Link key={team.id} to={`/teams/${team.id}`} className="rounded-[var(--radius-lg)] border border-border bg-surface p-4 shadow-xs hover:border-border-strong">
                <p className="font-semibold">{team.name}</p>
                <p className="text-xs text-fg-subtle">{team.description ?? team.slug}</p>
                <div className="mt-3 flex -space-x-1.5">
                  {team.members.slice(0, 8).map((m) => (
                    <span key={m.user.id} className="rounded-full ring-2 ring-surface">
                      <UserChip user={{ ...m.user, status: 'ACTIVE' }} />
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-fg-subtle">
                  {team.memberCount} {t('teams.members').toLowerCase()}
                </p>
              </Link>
            ))}
          </div>
        </QueryState>
      </div>
      <CreateTeamDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function CreateTeamDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const create = useApiMutation('createTeam', { invalidate: ['listTeams'] });
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('teams.new')}>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await create.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { name, slug, ...(description ? { description } : {}) } });
              onOpenChange(false);
            } catch (error) {
              toastError(error);
            }
          }}
        >
          <Field label={t('projects.name')} htmlFor="t-name" required>
            <Input
              id="t-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSlug(e.target.value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40));
              }}
            />
          </Field>
          <Field label={t('teams.slug')} htmlFor="t-slug" required>
            <Input id="t-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </Field>
          <Field label={t('projects.description')} htmlFor="t-desc">
            <Textarea id="t-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!name || !slug}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TeamPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const me = useMe();
  const team = useApiQuery('getTeam', { params: { id: id! } });
  const work = useApiQuery('listWorkItems', { query: { teamId: id!, limit: '30' } });
  const setMember = useApiMutation('setTeamMember', { invalidate: ['getTeam'] });
  const removeMember = useApiMutation('removeTeamMember', { invalidate: ['getTeam'] });
  const view = team.data?.data;
  const canManage = view?.availableActions.includes('manageMembers');
  return (
    <QueryState query={team}>
      {view ? (
        <div className="flex h-full flex-col">
          <PageHeader title={view.name} subtitle={view.description ?? undefined} />
          <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-6 p-4 md:p-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <Card className="p-4">
              <SectionTitle>{t('teams.members')}</SectionTitle>
              <ul className="flex flex-col gap-2">
                {view.members.map((m) => (
                  <li key={m.user.id} className="flex items-center gap-2">
                    <UserChip user={{ ...m.user, status: 'ACTIVE' }} />
                    {m.role === 'MANAGER' ? (
                      <Badge tone="accent">
                        <Shield /> {t('teams.manager')}
                      </Badge>
                    ) : null}
                    {canManage && (m.role !== 'MANAGER' || me.permissions.isAdmin) ? (
                      <button className="ml-auto text-fg-subtle hover:text-danger" aria-label={t('teams.remove')} onClick={() => void removeMember.mutateAsync({ params: { id: view.id, childId: m.user.id } }).catch(toastError)}>
                        <X className="size-3.5" />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
              {canManage ? (
                <div className="mt-4 flex flex-col gap-2">
                  <PrincipalPicker label={t('teams.addMember')} value={null} placeholder={t('teams.addMember')} allowClear={false} onChange={(p) => p && void setMember.mutateAsync({ params: { id: view.id }, body: { userId: p.id, role: 'MEMBER' } }).catch(toastError)} />
                  {me.permissions.isAdmin ? (
                    <NativeSelect
                      aria-label={t('teams.manager')}
                      value=""
                      onChange={(e) => e.target.value && void setMember.mutateAsync({ params: { id: view.id }, body: { userId: e.target.value, role: 'MANAGER' } }).catch(toastError)}
                    >
                      <option value="">{t('teams.manager')}…</option>
                      {view.members
                        .filter((m) => m.role === 'MEMBER')
                        .map((m) => (
                          <option key={m.user.id} value={m.user.id}>
                            {m.user.displayName}
                          </option>
                        ))}
                    </NativeSelect>
                  ) : null}
                </div>
              ) : null}
            </Card>
            <Card>
              <QueryState query={work} empty={work.data?.data.length === 0 ? <EmptyState title={t('work.empty')} /> : undefined}>
                <ul className="divide-y divide-border">
                  {(work.data?.data ?? []).map((w) => (
                    <li key={w.id}>
                      <Link to={`/work?selected=${w.id}&teamId=${view.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2">
                        <KeyLabel value={w.key} />
                        <span className="min-w-0 flex-1 truncate text-sm">{w.title}</span>
                        <UserChip user={w.assignee} />
                        <PhaseBadge phase={w.phase} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </QueryState>
            </Card>
          </div>
        </div>
      ) : null}
    </QueryState>
  );
}
